import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import useAppDispatch from 'src/hooks/useAppDispatch';
import color from 'src/theme/color';

import { IconButtonStyled } from 'src/pages/styled';

import { ButtonContentBox } from '../styled';
import { StyledButton, ButtonTextBox } from '../../styled';

import {
    ButtonIconBox,
    StyledDivider,
    StyledDialog,
    StyledDialogTitle,
    StyledDialogContent,
    StyledDialogActions,
    SearchTextField,
    SelectedSkillsContainer,
    SkillsContainer,
    SelectedSkillChip,
    SuggestedSkillChip,
    NoSkillsFoundContainer,
    ClearSearchButton,
    SectionTitle
} from './styled';

import { getSkillsFromState } from '../../../../../redux/onboarding/selectors';
import { getSkills } from '../../../../../redux/onboarding/service';
import { ProjectSkill } from '../../../../../types/projects';

interface SkillDialogProps {
    open: boolean;
    onClose: () => void;
    onAddSkills: (skills: ProjectSkill[]) => void;
    existingSkills: ProjectSkill[];
}

const SkillDialog: FunctionComponent<SkillDialogProps> = ({
    open,
    onClose,
    onAddSkills,
    existingSkills
}) => {
    const dispatch = useAppDispatch();

    const [selectedSkills, setSelectedSkills] = useState<ProjectSkill[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isSaveHovered, setSaveIsHovered] = useState(false);

    const allSkills = useSelector(getSkillsFromState);

    useEffect(() => {
        const fetchSkills = async () => {
            await dispatch(getSkills());
        };
        fetchSkills();
    }, [dispatch]);

    const existingSkillIds = existingSkills.map((skill) => skill.id);
    const selectedSkillIds = selectedSkills.map((skill) => skill.id);

    const suggestedSkills = allSkills.filter(
        (skill: ProjectSkill) =>
            !existingSkillIds.includes(skill.id) &&
            !selectedSkillIds.includes(skill.id) &&
            skill.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleRemoveSkill = (skillIdToRemove: string) => {
        setSelectedSkills(
            selectedSkills.filter((skill) => skill.id !== skillIdToRemove)
        );
    };

    const handleSelectSuggestion = (skill: ProjectSkill) => {
        setSelectedSkills([...selectedSkills, skill]);
    };

    const handleSave = () => {
        onAddSkills(selectedSkills);
        handleClose();
    };

    const handleClose = () => {
        setSelectedSkills([]);
        setSearchTerm('');
        onClose();
    };

    return (
        <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <StyledDialogTitle>
                <Typography variant="h6" component="div">
                    Add Skills
                </Typography>
                <IconButtonStyled
                    onClick={handleClose}
                    aria-label="close"
                    sx={{
                        position: 'absolute',
                        right: 8
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButtonStyled>
            </StyledDialogTitle>

            <StyledDivider />

            <StyledDialogContent>
                <SearchTextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search skills..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: color.white50 }} />
                            </InputAdornment>
                        )
                    }}
                />

                {selectedSkills.length > 0 && (
                    <SelectedSkillsContainer>
                        <SectionTitle variant="subtitle2">
                            Selected Skills
                        </SectionTitle>
                        <SkillsContainer>
                            {selectedSkills.map((skill) => (
                                <SelectedSkillChip
                                    key={skill.id}
                                    label={skill.name}
                                    onDelete={() => handleRemoveSkill(skill.id)}
                                />
                            ))}
                        </SkillsContainer>
                    </SelectedSkillsContainer>
                )}

                {suggestedSkills.length > 0 ? (
                    <Box>
                        <SectionTitle variant="subtitle2">
                            Suggested Skills
                        </SectionTitle>
                        <SkillsContainer>
                            {suggestedSkills.map((skill: ProjectSkill) => (
                                <SuggestedSkillChip
                                    key={skill.id}
                                    label={skill.name}
                                    onClick={() =>
                                        handleSelectSuggestion(skill)
                                    }
                                />
                            ))}
                        </SkillsContainer>
                    </Box>
                ) : (
                    <NoSkillsFoundContainer>
                        <Typography variant="body2" color={color.white50}>
                            No skills found matching your search criteria
                        </Typography>
                        {searchTerm && (
                            <ClearSearchButton
                                variant="text"
                                onClick={() => setSearchTerm('')}
                            >
                                Clear search
                            </ClearSearchButton>
                        )}
                    </NoSkillsFoundContainer>
                )}
            </StyledDialogContent>

            <StyledDialogActions>
                <ButtonContentBox>
                    <StyledButton
                        variant="contained"
                        fullWidth
                        onClick={handleSave}
                        onMouseEnter={() => setSaveIsHovered(true)}
                        onMouseLeave={() => setSaveIsHovered(false)}
                    >
                        <ButtonTextBox $isHovered={isSaveHovered}>
                            Add Skills
                        </ButtonTextBox>
                        <ButtonIconBox $isHovered={isSaveHovered}>
                            🐝
                        </ButtonIconBox>
                    </StyledButton>
                </ButtonContentBox>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default SkillDialog;
